import { withListOverflow } from "../../v2/components/ListOverflow.jsx";
export const TracksPanel = withListOverflow(TracksListOwner);
// src/features/projects/TracksPanel.jsx
//
// WPR Slice 1 — Tracks panel for the Project detail page.
//
// Shows the 7 canonical tracks as table rows:
//   Track name | Status dot | Owner | Note
//
// Viewer role: read-only, no edit affordances.
// Admin/editor: each row is editable inline (status selector + text inputs).
// Every save writes one PATCH per track and emits an audit_log row.

import React, { useState, useCallback } from "react";
import { T } from "../../app/tokens.js";
import { Card } from "../../components/Card.jsx";
import { ErrorBanner } from "../../components/ErrorBanner.jsx";
import { updateProjectTrack } from "../../data/projectsClient.js";
import { log } from "../../utils/logger.js";
import { PROJECT_TRACKS } from "../../lib/projectTracks.js";

// ─── Constants ────────────────────────────────────────────────────────────────

export const CANONICAL_TRACKS = PROJECT_TRACKS;

const TRACK_STATUSES = ["not_started", "green", "amber", "red"];

const STATUS_DOT = {
  green:       { color: T.green,      label: "Green"       },
  amber:       { color: T.amber,      label: "Amber"       },
  red:         { color: T.red,        label: "Red"         },
  not_started: { color: T.defaultDot, label: "Not started" },
};

const TH = {
  padding: "8px 10px",
  textAlign: "left",
  fontSize: T.fsXxs,
  fontWeight: T.fwDisplay,
  color: T.light,
  textTransform: "uppercase",
  letterSpacing: 0.4,
  borderBottom: `1px solid ${T.border}`,
  background: T.white,
};
const TD = { padding: "8px 10px", fontSize: T.fsSm5, color: T.dark, verticalAlign: "top" };

// ─── Status dot ───────────────────────────────────────────────────────────────

function StatusDot({ status }) {
  const cfg = STATUS_DOT[status] || STATUS_DOT.not_started;
  return (
    <span
      title={cfg.label}
      style={{
        display: "inline-block",
        width: 8, height: 8,
        borderRadius: "50%",
        background: cfg.color,
        marginRight: 6,
        flexShrink: 0,
      }}
    />
  );
}

// ─── Status selector (edit mode) ─────────────────────────────────────────────

function StatusSelect({ value, onChange }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        fontSize: T.fsXs, padding: "2px 4px", borderRadius: 4,
        border: `1px solid ${T.border}`, background: T.white,
        color: T.dark, cursor: "pointer",
      }}
    >
      {TRACK_STATUSES.map((s) => (
        <option key={s} value={s}>{STATUS_DOT[s]?.label || s}</option>
      ))}
    </select>
  );
}

// ─── Track row (edit mode) ────────────────────────────────────────────────────

function TrackRowEdit({ track, draft, onField, onSave, onCancel, saving }) {
  return (
    <tr style={{ background: T.blueSoft }}>
      <td style={{ ...TD, fontWeight: T.fwBold, whiteSpace: "nowrap" }}>
        {track.emoji} {track.label}
      </td>
      <td style={{ ...TD }}>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <StatusDot status={draft.status} />
          <StatusSelect value={draft.status} onChange={(v) => onField("status", v)} />
        </div>
        <input
          type="text"
          placeholder="Owner"
          value={draft.owner}
          onChange={(e) => onField("owner", e.target.value)}
          style={{
            marginTop: 4, fontSize: T.fsXs, padding: "3px 6px",
            border: `1px solid ${T.border}`, borderRadius: 4,
            width: "100%", color: T.dark,
          }}
        />
      </td>
      <td style={{ ...TD }}>
        <input
          type="text"
          placeholder="Note…"
          value={draft.note}
          onChange={(e) => onField("note", e.target.value)}
          style={{
            fontSize: T.fsSm, padding: "4px 6px",
            border: `1px solid ${T.border}`, borderRadius: 4,
            width: "100%", color: T.dark,
          }}
        />
      </td>
      <td style={{ ...TD, whiteSpace: "nowrap" }}>
        <button
          type="button"
          disabled={saving}
          onClick={onSave}
          style={{
            fontSize: T.fsXs, padding: "3px 9px", borderRadius: 5,
            background: T.blue, color: T.white, border: "none",
            cursor: saving ? "default" : "pointer", marginRight: 4,
            opacity: saving ? 0.65 : 1,
          }}
        >
          {saving ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={onCancel}
          style={{
            fontSize: T.fsXs, padding: "3px 9px", borderRadius: 5,
            background: "none", color: T.mid,
            border: `1px solid ${T.border}`, cursor: "pointer",
          }}
        >
          Cancel
        </button>
      </td>
    </tr>
  );
}

// ─── Track row (read mode) ────────────────────────────────────────────────────

function TrackRowRead({ track, data, canEdit, onEdit }) {
  return (
    <tr>
      <td style={{ ...TD, fontWeight: T.fwBold, whiteSpace: "nowrap" }}>
        {track.emoji} {track.label}
      </td>
      <td style={{ ...TD }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <StatusDot status={data?.status || "not_started"} />
          <span style={{ fontSize: T.fsXs5, color: T.mid, whiteSpace: "nowrap" }}>
            {data?.owner || "—"}
          </span>
        </div>
      </td>
      <td style={{ ...TD, color: T.dark }}>
        {data?.note || "—"}
      </td>
      {canEdit && (
        <td style={{ ...TD, whiteSpace: "nowrap" }}>
          <button
            type="button"
            onClick={onEdit}
            style={{
              fontSize: T.fsXs, padding: "2px 8px", borderRadius: 5,
              background: "none", color: T.blue,
              border: `1px solid ${T.barTintBorder}`, cursor: "pointer",
            }}
          >
            Edit
          </button>
        </td>
      )}
    </tr>
  );
}

// ─── TracksPanel ──────────────────────────────────────────────────────────────

/**
 * @param {object}   props
 * @param {string}   props.projectId  UUID of the project
 * @param {Array}    props.tracks     Array of track objects from getProjectTracks()
 * @param {Function} props.onTrackUpdated  Called with updated track after save
 * @param {boolean}  props.canEdit    Whether the current user may edit tracks
 */
function TracksListOwner({ projectId, tracks, onTrackUpdated, canEdit }) {
  const [editingKey, setEditingKey] = useState(null);
  const [draft,      setDraftState] = useState({});
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState(null);

  const setField = useCallback((field, value) => {
    setDraftState((prev) => ({ ...prev, [field]: value }));
  }, []);

  function handleEdit(trackKey, data) {
    setError(null);
    setEditingKey(trackKey);
    setDraftState({
      status: data?.status || "not_started",
      owner:  data?.owner  || "",
      note:   data?.note   || "",
    });
  }

  function handleCancel() {
    setEditingKey(null);
    setDraftState({});
    setError(null);
  }

  async function handleSave(trackKey) {
    setSaving(true);
    setError(null);
    try {
      const patch = {
        status: draft.status,
        owner:  draft.owner.trim() || null,
        note:   draft.note.trim()  || null,
      };
      const updated = await updateProjectTrack(projectId, trackKey, patch);
      log.ui("TracksPanel: track saved", { projectId, trackKey });
      onTrackUpdated(updated);
      setEditingKey(null);
      setDraftState({});
    } catch (err) {
      log.error("TracksPanel: save failed", { err: String(err), trackKey });
      setError(err.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  }

  // Build a lookup by track_key for quick access
  const byKey = {};
  if (Array.isArray(tracks)) {
    for (const t of tracks) {
      if (t?.trackKey) byKey[t.trackKey] = t;
    }
  }

  return (
    <Card style={{ padding: 0, overflow: "hidden" }}>
      {error && (
        <div style={{ padding: "12px 16px 0" }}>
          <ErrorBanner message={error} />
        </div>
      )}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ ...TH, width: 130 }}>Track</th>
              <th style={{ ...TH, width: 160 }}>Status / Owner</th>
              <th style={TH}>Update</th>
              {canEdit && <th style={{ ...TH, width: 100 }}></th>}
            </tr>
          </thead>
          <tbody>
            {CANONICAL_TRACKS.map((track, i) => {
              const data = byKey[track.key];
              const isEditing = editingKey === track.key;
              const bg = i % 2 === 1 ? T.lightGray : T.white;

              if (isEditing) {
                return (
                  <TrackRowEdit
                    key={track.key}
                    track={track}
                    draft={draft}
                    onField={setField}
                    onSave={() => handleSave(track.key)}
                    onCancel={handleCancel}
                    saving={saving}
                  />
                );
              }

              return (
                <tr key={track.key} style={{ background: bg }}>
                  <td style={{ ...TD, fontWeight: T.fwBold, whiteSpace: "nowrap" }}>
                    {track.emoji} {track.label}
                  </td>
                  <td style={{ ...TD }}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <StatusDot status={data?.status || "not_started"} />
                      <span style={{ fontSize: T.fsXs5, color: T.mid, whiteSpace: "nowrap" }}>
                        {data?.owner || "—"}
                      </span>
                    </div>
                  </td>
                  <td style={{ ...TD, color: T.dark }}>
                    {data?.note || "—"}
                  </td>
                  {canEdit && (
                    <td style={{ ...TD, whiteSpace: "nowrap" }}>
                      <button
                        type="button"
                        onClick={() => handleEdit(track.key, data)}
                        style={{
                          fontSize: T.fsXs, padding: "2px 8px", borderRadius: 5,
                          background: "none", color: T.blue,
                          border: `1px solid ${T.barTintBorder}`, cursor: "pointer",
                        }}
                      >
                        Edit
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
